import { Injectable } from '@angular/core';
import { isNil } from 'lodash';
import { NOT_APPLICABLE } from '../../../shared/constants/app.constants';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { DDFeatureConfigService } from 'src/app/shared/service/common/dd-feature-config.service';
import { decode } from 'html-entities';
import { VSpecService } from 'src/app/services/api/vspec.service';
import { FeatureConfigActionSet } from 'src/app/store/actions/feature-config.action';
import { SharedFunctionService } from 'src/app/services/common/shared-function.service';
import * as vspecJSONData from '../../../services/api/vspec-pdf-data-json';
import { DDMenuEnableDisableService } from 'src/app/shared/service/common/dd-menu-enable-disable.service';
import { Store } from '@ngrx/store';
import * as fromRootStore from '../../../store';
import { OmnitureTags } from 'src/app/shared/omnitureTags';
import { DealerDetailService } from 'src/app/services/api/dealer-detail.service';
import * as sharedDataActions from '../../../store/actions/shared-data.action';
import { VSpecPDFService } from '../../../services/api/vspec-pdf.service';
import { environment } from '../../../../environments/environment.prod';
import * as models from '../../../shared/models';

@Injectable({
  providedIn: 'root'
})
export class VSpecDetailService {
  sharedData: models.SharedData = <models.SharedData>{};
  dealerConfigSub: any;
  dealerDetailsLoaded$ = new BehaviorSubject<boolean>(false);
  vehicleDetails: any;
  dealerDetails: any;
  vin: any;
  dealerCd: any;
  deleteVin: boolean = false;
  showLoader = false;
  mpgDetails: any = null;
  vehicleInfoNotFound = false;
  vehicleInfoNotFoundMessage = "Page you are looking for is not found!";
  vehicleInfoNotFoundDirection = "Please contact dealer for more information.";
  incompleteVehicle = false;
  vehicleInfoIncompleteMessage = "VSPEC is not available until required Vehicle details are available."
  vehicleInfoIncompleteDirection = "Please try again later.";
  exteriorLabels = [];
  factoryaccessoriesTotalPrice: number = 0.00;
  portaccessoriesTotalPrice: number = 0.00;
  distributoraccessoriesTotalPrice: number = 0.00;
  dealerInstalledaccessoriesTotalPrice: number = 0.00;
  dealerAdjustments: number = 0.00;
  isDios: boolean = false;
  isSmartPathDealer: boolean = false;
  isGST: boolean = false;
  htmlRegEx = /(\[([^\]]+)\]|<([^>]+)>)/gi;
  isGas: boolean = false;
  isElectric: boolean = false;
  isHybrid: boolean = false;
  tsrpDisclaimer: string = '';
  baseMsrpDisclaimer: string = '';
  includeAdjustments: boolean = false;



  constructor(
    private _featureConfigService: DDFeatureConfigService,
    private vSpecService: VSpecService,
    public sharedFunctionService: SharedFunctionService,
    public store: Store<fromRootStore.IAppState>,
    private _ddMenuEnableDisableService: DDMenuEnableDisableService,
    public omTag: OmnitureTags,
    private dealerDetailService: DealerDetailService,
    private vspecPDFService: VSpecPDFService,
  ) {
    store.select(fromRootStore.SharedDataStore).subscribe(data => {
      this.sharedData = data;
    });
  }

  processCriticalData(vehicle) {
    vehicle.vin = vehicle.vin ?? NOT_APPLICABLE;
    vehicle.year = vehicle.year ?? NOT_APPLICABLE;
    vehicle.model = vehicle.model ?? {};
    vehicle.price = vehicle.price ?? {};
    vehicle.model.modelCd = vehicle.model.modelCd ?? NOT_APPLICABLE;
    vehicle.model.marketingName = vehicle.model.marketingName ?? NOT_APPLICABLE;
    vehicle.model.marketingTitle = vehicle.model.marketingTitle ?? NOT_APPLICABLE;
    vehicle.price.totalMsrp = vehicle.price.totalMsrp ?? NOT_APPLICABLE;
    vehicle.price.baseMsrp = vehicle.price.baseMsrp ?? NOT_APPLICABLE;
    vehicle.price.dph = vehicle.price.dph ?? NOT_APPLICABLE;
    vehicle.price.advertizedPrice = vehicle.price.advertizedPrice ?? NOT_APPLICABLE;
    vehicle.seriesCode = vehicle.seriesCode ?? NOT_APPLICABLE;
  
    return vehicle;
  }

  processData = (res, isSmartPathDealer) => {
    this.vehicleDetails = res;
    if (this.vehicleDetails.distributorCd && this.vehicleDetails.distributorCd === 'GST') {
      this.isGST = true;
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.vin)){
      this.vin = this.vehicleDetails.vin;
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.options)) {
      this.vehicleDetails.options = this.vehicleDetails.options
        .map(opt =>
          opt = (<any>Object).assign({}, opt,
            {
              marketingName: this.formatMarketingNames(opt.marketingName),
              marketingLongName: this.formatMarketingNames(opt.marketingLongName)
            })
        );
      let fios = this.vehicleDetails.options.filter(res => res.optionType === 'F');
      let dios = this.vehicleDetails.options.filter(res => res.optionType === 'D');
      this.isDios = dios.length > 0 ? true : false;
      let lios = this.vehicleDetails.options.filter(res => res.optionType === 'L');
      let pios = this.vehicleDetails.options.filter(res => res.optionType === 'P');

      this.vehicleDetails.options = [];
      this.vehicleDetails.options.push(...fios, ...pios, ...lios, ...dios);
    }

    const standardOptions = [
      'mechanicalAndPerformance',
      'steering',
      'toyotaCare',
      'offHighway',
      'audioMultimedia',
      'connectedServices'
    ];
    
    standardOptions.forEach(option => {
      this.vehicleDetails.standardOptions[option] = this.vehicleDetails.standardOptions[option] || [];
    });

    const { mechanicalAndPerformance, steering, offHighway, audioMultimedia, toyotaCare, connectedServices } = this.vehicleDetails.standardOptions;
    if (Array.isArray(steering)) {
      this.vehicleDetails.standardOptions.steering = steering.map(e => `${e.name} ${e.value}`);
    }
    if (Array.isArray(toyotaCare)) {
      this.vehicleDetails.standardOptions.toyotaCare = toyotaCare.map(e => `${e.name} ${e.value}`);
    }

    if (Array.isArray(offHighway)) {
      this.vehicleDetails.standardOptions.offHighway = offHighway.map(e => `${e.name} ${e.value}`);
    }

    if (Array.isArray(audioMultimedia)) {
      this.vehicleDetails.standardOptions.audioMultimedia = audioMultimedia.map(e => `${e.name} ${e.value}`);
    }

    if (Array.isArray(connectedServices)) {
      this.vehicleDetails.standardOptions.connectedServices = connectedServices.map(e => `${e.name} ${e.value}`);
    }

    if (this.vehicleDetails.drivetrain && this.vehicleDetails.drivetrain.bulletlist) {
      this.vehicleDetails.standardOptions.mechanicalAndPerformance = this.vehicleDetails.standardOptions.mechanicalAndPerformance.concat(this.vehicleDetails.drivetrain.bulletlist);
    }
    if (this.vehicleDetails.engine && this.vehicleDetails.engine.name) {
      this.vehicleDetails.standardOptions.mechanicalAndPerformance = this.vehicleDetails.standardOptions.mechanicalAndPerformance.concat(`Engine: ${this.vehicleDetails.engine.name}`);
    }
    if (this.vehicleDetails.transmission && this.vehicleDetails.transmission.transmissionType) {
      this.vehicleDetails.standardOptions.mechanicalAndPerformance = this.vehicleDetails.standardOptions.mechanicalAndPerformance.concat(this.vehicleDetails.transmission.transmissionType);
    }
    if (this.vehicleDetails.weightRating) {
      this.vehicleDetails.standardOptions.mechanicalAndPerformance = this.vehicleDetails.standardOptions.mechanicalAndPerformance.concat(`Weight Rating: ${this.vehicleDetails.weightRating}`);
    }
    if (this.vehicleDetails.standardOptions.exteriorDimensions) {
      this.exteriorLabels = this.vehicleDetails.standardOptions.exteriorDimensions.map(e => `${e.name} ${e.value}`)
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.extColor) && !isNil(this.vehicleDetails.extColor.marketingName)) {
      this.vehicleDetails.extColor.marketingName = this.formatMarketingNames(this.vehicleDetails.extColor.marketingName);
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.intColor) && !isNil(this.vehicleDetails.intColor.marketingName)) {
      this.vehicleDetails.intColor.marketingName = this.formatMarketingNames(this.vehicleDetails.intColor.marketingName);
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.standardOptions) &&
      !isNil(this.vehicleDetails.standardOptions.interior)) {
      this.vehicleDetails.standardOptions.interior = this.vehicleDetails.standardOptions.interior
        .map(interior => interior = this.formatMarketingNames(interior));
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.standardOptions) &&
      !isNil(this.vehicleDetails.standardOptions.safetyAndConvenience)) {
      this.vehicleDetails.standardOptions.safetyAndConvenience = this.vehicleDetails.standardOptions.safetyAndConvenience
        .map(safetyAndConvenience =>
          safetyAndConvenience = this.formatMarketingNames(safetyAndConvenience));
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.standardOptions) &&
      !isNil(this.vehicleDetails.standardOptions.exterior)) {
      this.vehicleDetails.standardOptions.exterior = this.vehicleDetails.standardOptions.exterior
        .map(exterior =>
          exterior = this.formatMarketingNames(exterior));
    }
    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.standardOptions) &&
      !isNil(this.vehicleDetails.standardOptions.mechanicalAndPerformance)) {
      this.vehicleDetails.standardOptions
        .mechanicalAndPerformance = this.vehicleDetails.standardOptions
          .mechanicalAndPerformance.map(mechPerformance =>
            mechPerformance = this.formatMarketingNames(mechPerformance));
    }
    this.mpgDetails = this.sharedFunctionService.populateMpgValues(this.vehicleDetails);

    if (isNil(this.mpgDetails.mpge.cityMpge) && !isNil(this.mpgDetails.mpg.cityMpg)) {
      this.isGas = true;
    } else if (isNil(this.mpgDetails.mpg.cityMpg) && !isNil(this.mpgDetails.mpge.cityMpge)) {
      this.isElectric = true;
    } else {
      this.isHybrid = true;
    }

    this.sharedData.vehicleDetails = this.vehicleDetails;
    this.sharedData.currentVin = this.vehicleDetails.vin;


    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.options)) {
      this.vehicleDetails.options.forEach(res => {
        if (res.optionType === 'F' && res.msrp) {
          this.factoryaccessoriesTotalPrice = this.factoryaccessoriesTotalPrice + res.msrp;
        }
      });
    }

    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.options)) {
      this.vehicleDetails.options.forEach(res => {
        if (res.optionType === 'P' && res.msrp) {
          this.portaccessoriesTotalPrice = this.portaccessoriesTotalPrice + res.msrp;
        }
      });
    }

    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.options)) {
      this.vehicleDetails.options.forEach(res => {
        if (res.optionType === 'L' && res.msrp) {
          this.distributoraccessoriesTotalPrice = this.distributoraccessoriesTotalPrice + res.msrp;
        }
      });
    }

    if (!isNil(this.vehicleDetails) && !isNil(this.vehicleDetails.options)) {
      this.vehicleDetails.options.forEach(res => {
        if (res.optionType === 'D' && res.dealerSellingPrice) {
          this.dealerInstalledaccessoriesTotalPrice = this.dealerInstalledaccessoriesTotalPrice + res.dealerSellingPrice;
        }
      });
    }

    // Save Shared Data
    //
    this.store.dispatch(new sharedDataActions.SetSharedDataAction(this.sharedData));
    this._ddMenuEnableDisableService.loadFeatureConfigData(this.vehicleDetails);
    this._ddMenuEnableDisableService.loadProtectionProducts(this.sharedData);
    this._addAdditionalDetails();

    if (isSmartPathDealer
      && this.vehicleDetails.price.advertizedPrice !== NOT_APPLICABLE
      && this.vehicleDetails.price.advertizedPrice > 0
    ) {
      this.dealerAdjustments = (this.vehicleDetails.price.advertizedPrice) - (this.vehicleDetails.price.totalMsrp);
    }

    return {
      vehicleDetails: this.vehicleDetails,
      dealerDetails: this.dealerDetails,
      isDios: this.isDios,
      isSmartPathDealer: this.isSmartPathDealer,
      isGST: this.isGST,
      isGas: this.isGas,
      isElectric: this.isElectric,
      isHybrid: this.isHybrid,
      mpgDetails: this.mpgDetails,
      dealerInstalledaccessoriesTotalPrice: this.dealerInstalledaccessoriesTotalPrice,
      distributoraccessoriesTotalPrice: this.distributoraccessoriesTotalPrice,
      portaccessoriesTotalPrice: this.portaccessoriesTotalPrice,
      factoryaccessoriesTotalPrice: this.factoryaccessoriesTotalPrice,
      dealerAdjustments: this.dealerAdjustments,
      baseMsrpDisclaimer: this.baseMsrpDisclaimer,
      tsrpDisclaimer: this.tsrpDisclaimer,
      vin: this.vin,
    };
  }

  private _addAdditionalDetails() {
    if (this.vehicleDetails.price && this.vehicleDetails.price.totalMsrp !== NOT_APPLICABLE) {
      const optionTypeD = this.vehicleDetails.options.filter(opt => opt.optionType === "D");
      if (optionTypeD && optionTypeD.length) {
        optionTypeD.forEach(opt => {
          this.vehicleDetails.price.optTotalMsrp += opt.dealerSellingPrice;
          this.vehicleDetails.price.totalMsrp += opt.dealerSellingPrice;
        });
      }
    }
  }

  isSmartPathDealer$(dealerCd) {
    return this._featureConfigService
      .get(dealerCd, null)
      .pipe(
        map(resp => {
          let isSmartPathDealer = false;
          if (resp.hasData()) {
            this.store.dispatch(new FeatureConfigActionSet(resp.data));
            const dealerConf = resp.data.filter(res => res.name === 'SMARTPATH' && res.active);
            if (dealerConf.length > 0) {
              isSmartPathDealer = true;
              localStorage.setItem('smartpathdealer', isSmartPathDealer.toString());
            } else {
              if (!isSmartPathDealer) {
                localStorage.setItem('smartpathdealer', 'false');
              }
            }
          }

          return isSmartPathDealer;
        }
      ));
  }

  getDealerDetails$(dealerCd: string) {
    return this.dealerDetailService.getDealerDetails(dealerCd, 'filterOnly', 'full', 'JSON', environment.DIS_API_KEY)
      .pipe(
        map(res => {
          this.dealerDetails = this.dealerDetailService.getFormattedDealerDetails(res);
          this.sharedData.dealerDetails = this.dealerDetails;
          this.store.dispatch(new sharedDataActions.SetSharedDataAction(this.sharedData));
          return this.dealerDetails;
        })
      )
  }

  getDisclaimers(args: any, isSmartPathDealer) {
    return isSmartPathDealer
      ? this.disclaimerSP(args)
      : this.disclaimerNonSP(args);
  }

  disclaimerSP(args){
    return this.vSpecService.getVehicleDisclaimersSP(args);
  }

  disclaimerNonSP(args){
    return forkJoin({
      baseMsrpDisclaimer: this.vSpecService.getVehicleDisclaimersSP(args),
      tsrpDisclaimer: this.vSpecService.getVehicleDisclaimersNonSp(args)
    });
  }

  formatFileName(deleteVin: boolean, vspecData: any) {
    return deleteVin
      ? `Vspec_${this.formatMarketingNames(vspecData.vehicleData.marketingSeries)}.pdf`
      : `Vspec_${this.formatMarketingNames(vspecData.vehicleData.marketingSeries)}_${vspecData.vehicleData.vin}.pdf`
  }

  /***
  * For removing the html code from the data
  */
  formatMarketingNames(data: string) {
    // remove any url is present in the string
    if (data !== null && data !== undefined && data.indexOf('.com') !== -1) {
      data = data.replace(/\(.*?\)/g, '');
    }
    if (data !== null && data !== undefined && data.indexOf('[') !== -1) {
      data = data.replace(/\[.*?\]/g, '');
    }

    return data;
  }

  downloadVSpecPDF(baseMrspDisclaimer: string, tsrpDisclaimer: string, novin?: boolean, customerNotes?: string, isPrintPDF? : boolean) {
    const vspecData: any = {};
    let carImageSrc = null;
    let intColorImageSrc = null;
    let extColorImageSrc = null;
    if (!isNil(this.vehicleDetails)) {
      const isSET = this.vehicleDetails.distributorCd === 'SET';

      this.vehicleDetails.options.forEach(opt => {
        if (isNil(opt.msrp)) {
          opt['msrp'] = 0;
        }
      });

      vspecData.vehicleData = this.vehicleDetails;
      vspecData.vehicleData.dealerDetails = this.dealerDetails;
      if (this.vehicleDetails.media && this.vehicleDetails.media.length > 0 && this.vehicleDetails.media[0].href) {
        if (isSET) {
          const mediaPortImages = this.vehicleDetails.media.filter(media => media.source === 'PORT');
          if (mediaPortImages.length > 0) {
            carImageSrc = mediaPortImages[0].href;
          }
        }
        if (!carImageSrc) {
          carImageSrc = this.vehicleDetails.media[0].href;
        }
      }
      if (this.vehicleDetails.intColor && this.vehicleDetails.intColor.colorSwatch) {
        intColorImageSrc = this.vehicleDetails.intColor.colorSwatch;
      }
      if (this.vehicleDetails.brand==='LEXUS' && this.vehicleDetails.extColor && this.vehicleDetails.extColor.colorSwatch) {
        extColorImageSrc = this.vehicleDetails.extColor.colorSwatch;
      }

      const images = [{
        src: carImageSrc,
        base64Data: undefined
      },
      {
        src: intColorImageSrc,
        base64Data: undefined
      },
      {
        src: extColorImageSrc,
        base64Data: undefined
      }];

      if (isNil(images[0].src)) {
        images[0].base64Data = vspecJSONData.VSpecPDFData.VSPEC_NO_VEHICLE_IMAGE.image;
      }
      if (isNil(images[1].src)) {
        images[1].base64Data = vspecJSONData.VSpecPDFData.VSPEC_NO_INT_IMAGE.image;
      }
      if (isNil(images[2].src)) {
        images[2].base64Data = vspecJSONData.VSpecPDFData.VSPEC_NO_INT_IMAGE.image;
      }

      let processedImgCount = 0;
      this.showLoader = true;
      images.map(image => {
        if (!isNil(image.src)) {
          this.sharedFunctionService.loadBase64DataFromImageURL(null,
            image.src, (carImageBase64Data) => {
              image.base64Data = carImageBase64Data;
              processedImgCount++;
              // Make sure all the images and Dealer Info loaded
              if (processedImgCount === images.length) {
                this.openVspecPDF(images, vspecData, baseMrspDisclaimer, tsrpDisclaimer, novin,customerNotes, isPrintPDF);
              }
            });
        } else {
          processedImgCount++;
          // Make sure all the images and Dealer Info loaded
          if (processedImgCount === images.length) {
            this.openVspecPDF(images, vspecData, baseMrspDisclaimer, tsrpDisclaimer, novin, customerNotes, isPrintPDF);
          }
        }
      });
    }
  }

  openVspecPDF(images: Array<any>, vspecData: any, baseMrspDisclaimer : string, tsrpDisclaimer : string, novin: boolean,customerNotes?: string, isPrintPDF?:boolean) {
    vspecData.carImage = images[0].base64Data || vspecJSONData.VSpecPDFData.VSPEC_NO_VEHICLE_IMAGE.image;
    vspecData.intColorImage = images[1].base64Data || vspecJSONData.VSpecPDFData.VSPEC_NO_INT_IMAGE.image;
    vspecData.extColorImage = images[2].base64Data || vspecJSONData.VSpecPDFData.VSPEC_NO_INT_IMAGE.image;
    if(vspecData.vehicleData.mpg && !Object.keys(vspecData.vehicleData.mpg).length){
      vspecData.vehicleData.mpg = {
        "city": ' ',
        "highway": ' ',
        "combined":' '
      }
    }
    if(vspecData.vehicleData.mpge && !Object.keys(vspecData.vehicleData.mpge).length){
      vspecData.vehicleData.mpge = {
        "combined": ' '
      }
    }
    // If the images are converted and all the required vehicle details,
    // dealer details are reterived.
    if (vspecData.vehicleData.dealerDetails !== null) {
      if(isPrintPDF) {
        this.vspecPDFService.getPDFWriter(vspecData, baseMrspDisclaimer,tsrpDisclaimer,novin,customerNotes,isPrintPDF).print();
        return;
      }
      this.vspecPDFService.getPDFWriter(vspecData, baseMrspDisclaimer,tsrpDisclaimer,novin).download(
      this.formatFileName(novin,vspecData));
    }

    this.showLoader = false;
  }
  
}
